import { Center, Flex } from '@chakra-ui/react';
import { GenderEnum } from '@halo/chat-sdk';
import dayjs from 'dayjs';

interface GenderAgeProps {
  gender?: GenderEnum;
  age?: string;
}

const GenderAge = ({ gender, age: ageNum }: GenderAgeProps) => {
  const age = useMemo(() => {
    if (!ageNum) return '';
    const ms = +ageNum;
    const isSameYear = dayjs(ms).isSame(dayjs(), 'year');
    if (isSameYear) {
      return '0';
    }
    return `${dayjs().diff(dayjs(ms), 'year')}`;
  }, [ageNum]);
  const genderIcon = useMemo(() => {
    switch (gender) {
      case GenderEnum.MALE:
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="8"
            height="8"
            viewBox="0 0 8 8"
            fill="none"
          >
            <path
              d="M5.26367 0.52002H7.24004V2.49639"
              stroke="white"
              strokeWidth="1.04"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M7.23939 0.52002L4.57129 3.18812"
              stroke="white"
              strokeWidth="1.04"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M2.89118 7.23987C4.201 7.23987 5.26282 6.17805 5.26282 4.86823C5.26282 3.5584 4.201 2.49658 2.89118 2.49658C1.58135 2.49658 0.519531 3.5584 0.519531 4.86823C0.519531 6.17805 1.58135 7.23987 2.89118 7.23987Z"
              stroke="white"
              strokeWidth="1.04"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        );
      case GenderEnum.FEMALE:
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="8"
            height="8"
            viewBox="0 0 8 8"
            fill="none"
          >
            <path
              d="M4.96484 -0.0400391C5.78613 -0.0399206 6.55892 0.279604 7.13965 0.860352C7.72039 1.44108 8.03993 2.21387 8.04004 3.03516C8.04004 3.8564 7.72023 4.62916 7.13965 5.20996C6.5589 5.79071 5.78613 6.11122 4.96484 6.11133C4.307 6.11133 3.68104 5.90461 3.15918 5.52441L2.68848 5.99512L3.90723 7.21387C4.09623 7.4029 4.09626 7.70942 3.90723 7.89844C3.7182 8.0873 3.41163 8.08738 3.22266 7.89844L2.00391 6.67871L1.54883 7.13477C1.3598 7.32379 1.05428 7.3238 0.865234 7.13477C0.676215 6.94575 0.676215 6.64019 0.865234 6.45117L1.32031 5.99512L0.101562 4.77734C-0.087377 4.58836 -0.0873036 4.28179 0.101562 4.09277C0.29058 3.90376 0.597099 3.90378 0.786133 4.09277L2.00391 5.31055L2.47461 4.83984C2.09493 4.31822 1.88866 3.69253 1.88867 3.03516C1.88878 2.21386 2.20928 1.4411 2.79004 0.860352C3.37083 0.279773 4.1436 -0.0400391 4.96484 -0.0400391ZM4.96484 0.927734C4.40203 0.927734 3.87266 1.14708 3.47461 1.54492C3.0766 1.94292 2.85754 2.47229 2.85742 3.03516C2.85742 3.59816 3.0765 4.12729 3.47461 4.52539C3.87272 4.92349 4.40184 5.14355 4.96484 5.14355C5.52769 5.14345 6.05708 4.92339 6.45508 4.52539C6.85293 4.12733 7.07227 3.59797 7.07227 3.03516C7.07216 2.47231 6.85308 1.94292 6.45508 1.54492C6.05708 1.14691 5.52769 0.927853 4.96484 0.927734Z"
              fill="white"
              stroke="white"
              strokeWidth="0.08"
            />
          </svg>
        );
      case undefined:
        return null;
      default:
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
          >
            <path
              d="M7.3125 5.25C7.46168 5.25 7.60447 5.30955 7.70996 5.41504C7.81545 5.52053 7.875 5.66332 7.875 5.8125V9.5625C7.875 9.83331 7.82139 10.1014 7.71777 10.3516C7.61412 10.6018 7.46203 10.829 7.27051 11.0205C7.07899 11.212 6.8518 11.3641 6.60156 11.4678C6.35136 11.5714 6.08331 11.625 5.8125 11.625C5.54169 11.625 5.27364 11.5714 5.02344 11.4678C4.7732 11.3641 4.54601 11.212 4.35449 11.0205C4.16297 10.829 4.01088 10.6018 3.90723 10.3516C3.80361 10.1014 3.75 9.83331 3.75 9.5625V8.8125C3.75 8.66332 3.80955 8.52053 3.91504 8.41504C4.02053 8.30955 4.16332 8.25 4.3125 8.25C4.46168 8.25 4.60447 8.30955 4.70996 8.41504C4.81545 8.52053 4.875 8.66332 4.875 8.8125V9.5625C4.875 9.68549 4.89927 9.80725 4.94629 9.9209C4.9934 10.0346 5.06236 10.1385 5.14941 10.2256C5.23647 10.3126 5.34036 10.3816 5.4541 10.4287C5.56775 10.4757 5.68951 10.5 5.8125 10.5C5.93549 10.5 6.05725 10.4757 6.1709 10.4287C6.28464 10.3816 6.38853 10.3126 6.47559 10.2256C6.56264 10.1385 6.6316 10.0346 6.67871 9.9209C6.72573 9.80725 6.75 9.68549 6.75 9.5625V5.8125C6.75 5.66332 6.80955 5.52053 6.91504 5.41504C7.02053 5.30955 7.16332 5.25 7.3125 5.25ZM2.8125 3.75C2.96168 3.75 3.10447 3.80955 3.20996 3.91504C3.31545 4.02053 3.375 4.16332 3.375 4.3125C3.375 4.46168 3.31545 4.60447 3.20996 4.70996C3.10447 4.81545 2.96168 4.875 2.8125 4.875H2.0625C1.93951 4.875 1.81775 4.89927 1.7041 4.94629C1.59036 4.9934 1.48647 5.06236 1.39941 5.14941C1.31236 5.23647 1.2434 5.34036 1.19629 5.4541C1.14927 5.56775 1.125 5.68951 1.125 5.8125C1.125 5.93549 1.14927 6.05725 1.19629 6.1709C1.2434 6.28464 1.31236 6.38853 1.39941 6.47559C1.48647 6.56264 1.59036 6.6316 1.7041 6.67871C1.81775 6.72573 1.93951 6.75 2.0625 6.75H5.8125C5.96168 6.75 6.10447 6.80955 6.20996 6.91504C6.31545 7.02053 6.375 7.16332 6.375 7.3125C6.375 7.46168 6.31545 7.60447 6.20996 7.70996C6.10447 7.81545 5.96168 7.875 5.8125 7.875H2.0625C1.79169 7.875 1.52364 7.82139 1.27344 7.71777C1.0232 7.61412 0.796013 7.46203 0.604492 7.27051C0.412971 7.07899 0.260877 6.8518 0.157227 6.60156C0.053606 6.35136 0 6.08331 0 5.8125C2.34766e-08 5.54169 0.0536059 5.27364 0.157227 5.02344C0.260877 4.7732 0.412971 4.54601 0.604492 4.35449C0.796013 4.16297 1.0232 4.01088 1.27344 3.90723C1.52364 3.80361 1.79169 3.75 2.0625 3.75H2.8125ZM9.5625 3.75C9.83331 3.75 10.1014 3.80361 10.3516 3.90723C10.6018 4.01088 10.829 4.16297 11.0205 4.35449C11.212 4.54601 11.3641 4.7732 11.4678 5.02344C11.5714 5.27364 11.625 5.54169 11.625 5.8125C11.625 6.08331 11.5714 6.35136 11.4678 6.60156C11.3641 6.8518 11.212 7.07899 11.0205 7.27051C10.829 7.46203 10.6018 7.61412 10.3516 7.71777C10.1014 7.82139 9.83331 7.875 9.5625 7.875H8.8125C8.66332 7.875 8.52053 7.81545 8.41504 7.70996C8.30955 7.60447 8.25 7.46168 8.25 7.3125C8.25 7.16332 8.30955 7.02053 8.41504 6.91504C8.52053 6.80955 8.66332 6.75 8.8125 6.75H9.5625C9.81114 6.75 10.0498 6.6514 10.2256 6.47559C10.4014 6.29977 10.5 6.06114 10.5 5.8125C10.5 5.56386 10.4014 5.32523 10.2256 5.14941C10.0498 4.9736 9.81114 4.875 9.5625 4.875H5.8125C5.66332 4.875 5.52053 4.81545 5.41504 4.70996C5.30955 4.60447 5.25 4.46168 5.25 4.3125C5.25 4.16332 5.30955 4.02053 5.41504 3.91504C5.52053 3.80955 5.66332 3.75 5.8125 3.75H9.5625ZM5.8125 0C6.08331 0 6.35136 0.053606 6.60156 0.157227C6.8518 0.260877 7.07899 0.412972 7.27051 0.604492C7.46203 0.796013 7.61412 1.0232 7.71777 1.27344C7.82139 1.52364 7.875 1.79169 7.875 2.0625V2.8125C7.875 2.96168 7.81545 3.10447 7.70996 3.20996C7.60447 3.31545 7.46168 3.375 7.3125 3.375C7.16332 3.375 7.02053 3.31545 6.91504 3.20996C6.80955 3.10447 6.75 2.96168 6.75 2.8125V2.0625C6.75 1.81386 6.6514 1.57523 6.47559 1.39941C6.29977 1.2236 6.06114 1.125 5.8125 1.125C5.56386 1.125 5.32523 1.2236 5.14941 1.39941C4.9736 1.57523 4.875 1.81386 4.875 2.0625V5.8125C4.875 5.96168 4.81545 6.10447 4.70996 6.20996C4.60447 6.31545 4.46168 6.375 4.3125 6.375C4.16332 6.375 4.02053 6.31545 3.91504 6.20996C3.80955 6.10447 3.75 5.96168 3.75 5.8125V2.0625C3.75 1.79169 3.80361 1.52364 3.90723 1.27344C4.01088 1.0232 4.16297 0.796013 4.35449 0.604492C4.54601 0.412971 4.7732 0.260877 5.02344 0.157227C5.27364 0.0536059 5.54169 2.34767e-08 5.8125 0Z"
              fill="white"
            />
          </svg>
        );
    }
  }, [gender]);

  const bgColor = useMemo(() => {
    if (gender === GenderEnum.MALE) return '#489EFF';
    if (gender === GenderEnum.FEMALE) return '#FF80B5';
    if (gender === undefined) {
      return 'brand.50';
    }
    return '#00B600';
  }, [gender]);

  if (!genderIcon && !age) return null;

  return (
    <Flex
      alignItems="center"
      justifyContent="center"
      px="3px"
      py="1px"
      gap="2px"
      minW="27px"
      h="16px"
      bg={bgColor}
      rounded="full"
      color="#fff"
    >
      <Center>{genderIcon}</Center>
      {age && (
        <Center fontSize="20px" zoom={0.5}>
          {age}
        </Center>
      )}
    </Flex>
  );
};

const MemoizedGenderAge = memo(GenderAge);
export { MemoizedGenderAge as GenderAge };
